import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import PanelTemplate from "../../../templates/Panel";
import PageHeader from "../../../components/PageHeader";
import Box from "../../../components/Box";
import Button from "../../../components/Button";
import Input from "../../../components/Input";
import InputSelect from "../../../components/InputSelect";
import InputTextarea from "../../../components/InputTextarea";
import Icon from "../../../components/Icon";
import SelectCustomer from "../../../components/selects/SelectCustomer";
import SelectCustomerCompany from "../../../components/selects/SelectCustomerCompany";
import SelectShippingCompany from "../../../components/selects/SelectShippingCompany";
import Modal from "../../../components/Modal";
import BoxTabs from "../../../components/BoxTabs";
import Toggle from "../../../components/Toggle";
import TaxRuleFields from "../components/TaxRuleFields";
import DocumentProblem from "../components/DocumentProblem";
import ModalReturnItems from "./ModalReturnItems";
import usePaymentOptions from "../../../components/PaymentCondition/usePaymentOptions";
import useNewPaymentTerm from "../../../components/PaymentCondition/useNewPaymentTerm";

import api from "../../../services/api";
import states from "../../../seeds/states";
import serviceCodes from "../../../seeds/serviceCodes";
import { renderError } from "../../../helpers/errors";
import { createOptions } from "../../../helpers/selects";
import { convertIntToMoney, convertMoenyToInt } from "../../../helpers/conversions";
import {
    CSOSN_OPTIONS,
    CST_OPTIONS,
    FISCAL_ORIGINS,
    formatCfop,
    itemTaxTabs,
    PAYMENT_NONE,
    UNPAID_PURPOSES,
    PRESENCE_INDICATOR_OPTIONS,
    isSimplesRegime,
} from "../helpers";

// Modalidade do frete (X02 da NF-e).
const FREIGHT_MODES = [
    { label: 'Por conta do emitente (CIF)', value: '0' },
    { label: 'Por conta do destinatário (FOB)', value: '1' },
    { label: 'Por conta de terceiros', value: '2' },
    { label: 'Transporte próprio, por conta do emitente', value: '3' },
    { label: 'Transporte próprio, por conta do destinatário', value: '4' },
    { label: 'Sem transporte', value: '9' },
];

// Totais que a nota carrega quando o cálculo automático está desligado.
const TOTAL_KEYS = [
    'products', 'icms_base', 'icms_value', 'icms_st_base', 'icms_st_value',
    'ipi_value', 'issqn_value', 'pis_value', 'cofins_value',
    'fcp_value', 'fcp_st_value', 'icms_exempt_value', 'total',
];

/**
 * Centavos → campo com máscara de dinheiro. Zero vira vazio: na tela,
 * "sem frete" e "frete de R$ 0,00" são a mesma coisa, e deixar o campo em
 * branco é o que o usuário reconhece como não preenchido.
 */
const moneyField = cents => cents ? convertIntToMoney(cents) : '';

const DAY = 24 * 60 * 60 * 1000;

const daysUntil = date => {
    if (!date) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return Math.round((new Date(`${date}T00:00:00`) - today) / DAY);
};

const describeDue = days => {
    if (days === null) return '';
    if (days < 0) return 'data já passou';
    if (days === 0) return 'à vista';

    return days === 1 ? 'em 1 dia' : `em ${days} dias`;
};

const addDaysTo = (date, days) => {
    const base = date ? new Date(`${date}T00:00:00`) : new Date();
    base.setDate(base.getDate() + days);

    return [base.getFullYear(), String(base.getMonth() + 1).padStart(2, '0'), String(base.getDate()).padStart(2, '0')].join('-');
};

/**
 * Reabre o formulário a partir do que foi salvo (`form_data`).
 *
 * É o inverso exato do payload montado em `issue()` — por isso os dois
 * ficam lado a lado neste arquivo: mexeu em um, mexe no outro.
 */
const stateFromFormData = data => ({
    model: data.model ?? 'nfe',
    companyId: data.company_id ?? null,
    receiverType: data.receiver_type ?? 'PJ',
    customerId: data.customer_id ?? null,
    customerCompanyId: data.customer_company_id ?? null,
    fiscalOperationId: data.fiscal_operation_id ?? '',
    referencedKeys: (data.referenced_keys ?? []).join(', '),
    presenceIndicator: data.presence_indicator ?? '9',
    items: (data.items ?? []).map(item => ({
        description: item.description ?? '',
        product_id: item.product_id ?? null,
        code: item.code ?? '',
        ncm: item.ncm ?? '',
        unit: item.unit ?? '',
        cest: item.cest ?? '',
        gtin: item.gtin ?? '',
        origin: item.origin ?? '',
        gross_weight: item.gross_weight ?? '',
        net_weight: item.net_weight ?? '',
        additional_info: item.additional_info ?? '',
        cfop: item.cfop ?? '',
        compose_total: item.compose_total !== false,
        taxes: item.taxes ?? {},
        quantity: item.quantity ?? '',
        unitPrice: moneyField(item.unit_price),
        referenced_item: item.referenced_item ?? '',
        referenced_key: item.referenced_key ?? '',
    })),
    // Nota salva antes do cadastro de formas (BDM-1208) só tem o código da
    // SEFAZ: a forma é achada por ele quando o cadastro carrega.
    paymentMethod: data.payment_method_id
        ?? (data.payment_method === PAYMENT_NONE ? PAYMENT_NONE : null),
    paymentMethodCode: data.payment_method_id ? null : (data.payment_method ?? null),
    freight: moneyField(data.freight),
    insurance: moneyField(data.insurance),
    otherExpenses: moneyField(data.other_expenses),
    discount: moneyField(data.discount),
    autoTaxes: data.auto_tax_calculation !== false,
    manualTotals: Object.fromEntries(
        Object.entries(data.totals ?? {}).map(([key, value]) => [key, moneyField(value)])
    ),
    freightMode: data.freight_mode ?? '0',
    carrier: {
        name: data.carrier_name ?? '',
        document: data.carrier_document ?? '',
        ie: data.carrier_ie ?? '',
        street: data.carrier_street ?? '',
        city: data.carrier_city ?? '',
        state: data.carrier_state ?? '',
    },
    volume: {
        quantity: data.volume_quantity ?? '',
        kind: data.volume_kind ?? '',
        brand: data.volume_brand ?? '',
        number: data.volume_number ?? '',
    },
    vehicle: { plate: data.vehicle_plate ?? '', state: data.vehicle_state ?? '' },
    paymentTerm: data.payment_term ?? '',
    paymentTermId: data.payment_term_id ?? null,
    installments: (data.installments ?? []).map(installment => ({
        due_date: installment.due_date,
        amount: moneyField(installment.amount),
    })),
    comments: data.comments ?? '',
    serviceDescription: data.service_description ?? '',
    serviceAmount: moneyField(data.amount),
    serviceCode: data.service_code ?? '',
    cityServiceCode: data.city_service_code ?? '',
    issRate: data.iss_rate ?? '',
    issWithheld: !!data.iss_withheld,
    withholdings: {
        pis: data.withholdings?.pis ?? '',
        cofins: data.withholdings?.cofins ?? '',
        csll: data.withholdings?.csll ?? '',
        inss: data.withholdings?.inss ?? '',
        irrf: data.withholdings?.irrf ?? '',
        cpp: data.withholdings?.cpp ?? '',
        others: moneyField(data.withholdings?.others),
    },
});

// Retenções federais da NFS-e: quem recolhe é o TOMADOR, e o prestador
// recebe o líquido. São alíquotas — o valor sai da base do serviço, calculado
// pelo emissor —, menos "outras retenções", que o schema pede em valor.
const WITHHOLDING_FIELDS = [
    { key: 'pis', label: 'PIS (%)' },
    { key: 'cofins', label: 'COFINS (%)' },
    { key: 'csll', label: 'CSLL (%)' },
    { key: 'irrf', label: 'IRRF (%)' },
    { key: 'inss', label: 'INSS (%)' },
    { key: 'cpp', label: 'CPP (%)' },
];

const RECEIVER_TYPES = [
    { label: 'Pessoa jurídica', value: 'PJ' },
    { label: 'Pessoa física', value: 'PF' },
];

/**
 * Linha da nota. Além do básico, carrega o que o produto do cadastro
 * preenche (SKU, unidade, CEST, origem…) e os ajustes fiscais que o usuário
 * pode fazer só para esta nota — `taxes` vazio significa "usa a natureza".
 */
const emptyItem = () => ({
    description: '',
    product_id: null,
    code: '',
    ncm: '',
    unit: '',
    cest: '',
    gtin: '',
    origin: '',
    // Peso POR UNIDADE, em GRAMAS — mesma unidade do cadastro de produto.
    // O volume da nota multiplica pela quantidade e converte para kg.
    gross_weight: '',
    net_weight: '',
    additional_info: '',
    cfop: '',
    compose_total: true,
    taxes: {},
    quantity: '',
    unitPrice: '',
    // Devolução: de qual linha da nota original este item veio.
    referenced_item: '',
    referenced_key: '',
});

/** Ajuste de imposto em branco — o que o modal mostra antes de mexerem. */
const emptyTaxRule = () => ({ cst: '', rate: '', base: '', attributes: {}, notes: '', fisco_notes: '' });

/** Item preenchido a partir de um produto do cadastro. */
const itemFromProduct = (item, product) => ({
    ...item,
    description: product.name ?? item.description,
    product_id: product.id ?? null,
    code: product.sku ?? '',
    ncm: product.ncm ?? '',
    unit: product.fiscal_unit ?? '',
    cest: product.cest ?? '',
    gtin: product.gtin ?? '',
    origin: product.fiscal_origin !== null && product.fiscal_origin !== undefined
        ? String(product.fiscal_origin)
        : '',
    gross_weight: product.weight ?? '',
    net_weight: product.net_weight ?? '',
});

// Campos do cliente editáveis nesta tela — os que saem impressos na nota.
// Tudo vira string: a API pode devolver número (ex.: "número" do endereço).
const asText = value => value === null || value === undefined ? '' : String(value);

const receiverFormFrom = (resource, isCompany) => ({
    name: asText(isCompany ? resource.company_name : resource.name),
    document: asText(isCompany ? resource.cnpj : resource.cpf),
    ie: asText(resource.ie),
    email: asText(resource.email),
    zip_code: asText(resource.zip_code),
    street: asText(resource.street),
    number: asText(resource.number),
    complement: asText(resource.complement),
    district: asText(resource.district),
    city: asText(resource.city),
    state: asText(resource.state),
});

/**
 * Descrição do item: busca no cadastro de produtos, mas NÃO obriga a
 * escolher um. Nota avulsa às vezes descreve algo que não está no catálogo
 * (uma amostra, um serviço embutido), então a lista é sugestão — o que for
 * digitado vale.
 */
const ItemDescription = ({ value, onPick, onType }) => {
    const [options, setOptions] = useState([]);
    const [noOptionsText, setNoOptionsText] = useState('Digite para buscar no catálogo');
    const controller = useRef(null);

    const search = term => {
        if ((term ?? '').length < 2) {
            controller.current?.abort();
            setOptions([]);
            setNoOptionsText('Digite para buscar no catálogo');
            return;
        }

        controller.current?.abort();
        controller.current = new AbortController();
        setNoOptionsText('Buscando...');

        api.get(`/product/search?term=${encodeURIComponent(term)}&per_page=8`, {
            signal: controller.current.signal,
        }).then(res => {
            const resources = res.data.resources?.data ?? [];

            // O valor da opção é o próprio nome, não o id: o campo guarda a
            // descrição que vai sair na nota, e ela precisa continuar
            // legível mesmo depois que a lista de sugestões se renova.
            setOptions(resources.map(product => ({
                label: product.sku ? `${product.name} · ${product.sku}` : product.name,
                value: product.name,
                product,
            })));
            setNoOptionsText(resources.length ? '' : 'Nenhum produto encontrado — pode digitar assim mesmo');
        }).catch(() => {});
    };

    return (
        <InputSelect
            creatable
            removeMargin
            filterOption={true}
            noOptionsText={noOptionsText}
            options={options}
            value={value}
            setSearch={search}
            createLabel={input => `Usar "${input}"`}
            change={selected => {
                const option = options.find(item => item.value === selected);

                if (option) {
                    onPick(option.product);
                    return;
                }

                onType(selected);
            }}
        />
    );
};

/**
 * Emissão manual de nota fiscal (BDM-887) — tela dedicada (vai crescer:
 * duplicar nota, vincular pedido depois, rascunho...).
 *
 * O cliente vem do CADASTRO (PF ou PJ), e os dados que saem impressos na
 * nota ficam EDITÁVEIS aqui mesmo: corrigiu, a emissão salva no cadastro e
 * emite — sem pular para outra tela. CFOP e tributação usam os padrões das
 * configurações fiscais; o usuário leigo só descreve o que vendeu.
 */
export const FiscalIssueDocument = () => {
    const [loadingIssuers, setLoadingIssuers] = useState(true);
    const [issuers, setIssuers] = useState([]);
    const [selectedIssuerId, setSelectedIssuerId] = useState(null);

    const [model, setModel] = useState('nfe');
    const [receiverType, setReceiverType] = useState('PJ');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedCustomerCompany, setSelectedCustomerCompany] = useState(null);

    // Cadastro completo (para o PUT não perder campos) + o recorte editável.
    const [receiverResource, setReceiverResource] = useState(null);
    const [receiverForm, setReceiverForm] = useState(null);
    const [loadingReceiver, setLoadingReceiver] = useState(false);

    const [items, setItems] = useState([emptyItem()]);
    // Id da forma cadastrada, ou PAYMENT_NONE (sem pagamento não é forma
    // cadastrável: é escolha da nota).
    const [paymentMethod, setPaymentMethod] = useState(null);
    const legacyPaymentCode = useRef(null);

    const [serviceDescription, setServiceDescription] = useState('');
    const [serviceAmount, setServiceAmount] = useState('');

    // Cada nota declara o serviço prestado — não há padrão na configuração.
    const [serviceCode, setServiceCode] = useState('');
    const [cityServiceCode, setCityServiceCode] = useState('');
    const [issRate, setIssRate] = useState('');

    // Retenções da NFS-e: nascem vazias porque reter é a exceção, não a regra
    // — e o que vale para um tomador não vale para o próximo.
    const [issWithheld, setIssWithheld] = useState(false);
    const [withholdings, setWithholdings] = useState({
        pis: '', cofins: '', csll: '', inss: '', irrf: '', cpp: '', others: '',
    });
    const [showWithholdings, setShowWithholdings] = useState(false);

    const [issuing, setIssuing] = useState(false);

    // Natureza de operação da nota — vem da configuração da empresa e pode
    // ser trocada aqui (uma remessa não é tributada como uma venda).
    const [operations, setOperations] = useState([]);
    const [fiscalOperationId, setFiscalOperationId] = useState('');
    // Devolução (finalidade 4) referencia a NF-e original — texto livre com
    // uma ou mais chaves de 44 dígitos.
    const [referencedKeys, setReferencedKeys] = useState('');
    // Indicador de presença (B11b): como a venda aconteceu. O 9 é o neutro.
    const [presenceIndicator, setPresenceIndicator] = useState('9');

    // O que a natureza resolve para cada linha (CFOP e situação tributária).
    // Vem do backend porque a resolução tem regra base, exceção por UF/NCM e
    // regime da empresa — reimplementar isso aqui criaria uma segunda verdade.
    const [preview, setPreview] = useState([]);
    // Observações que a natureza acrescenta à nota — mostradas em campo
    // travado para o usuário ver o que segue junto do que ele escreveu.
    const [operationNotes, setOperationNotes] = useState({ taxpayer: '', fisco: '' });

    // Cálculo de impostos: valores da nota inteira + o que a natureza
    // resolve. Com o cálculo automático desligado, os impostos passam a ser
    // digitados e vão para a nota como o usuário informou.
    const [freight, setFreight] = useState('');
    const [insurance, setInsurance] = useState('');
    const [otherExpenses, setOtherExpenses] = useState('');
    const [discount, setDiscount] = useState('');
    const [autoTaxes, setAutoTaxes] = useState(true);
    const [showAllTotals, setShowAllTotals] = useState(false);
    const [manualTotals, setManualTotals] = useState({});
    const [totals, setTotals] = useState(null);

    // Recalculando: liga na tecla digitada, não quando a requisição sai —
    // o intervalo de espera do debounce é justamente o que dava a impressão
    // de que a tela tinha travado.
    const [calculating, setCalculating] = useState(false);
    // Só a última resposta vale: digitar rápido dispara pedidos em sequência
    // e o mais antigo pode chegar depois, sobrescrevendo o número certo.
    const previewRequest = useRef(0);

    // Parcelamento e dados adicionais.
    const { terms: paymentTerms, methods: paymentMethods, loading: loadingPaymentOptions, addTerm: addPaymentTerm } = usePaymentOptions('sale');
    const [paymentTerm, setPaymentTerm] = useState('');
    const [paymentTermId, setPaymentTermId] = useState(null);
    const [installments, setInstallments] = useState([]);
    // Enquanto ninguém mexe nas parcelas, elas acompanham a condição e o
    // total da nota; editou uma linha, passam a ser do usuário.
    const [installmentsEdited, setInstallmentsEdited] = useState(false);
    const paymentTouched = useRef(false);
    const installmentsRequest = useRef(0);
    const [comments, setComments] = useState('');

    const selectPaymentTerm = term => {
        paymentTouched.current = true;
        setPaymentTermId(term.id);
        setPaymentTerm(term.name);
        setInstallmentsEdited(false);

        if (!paymentMethod && term.payment_method_id) {
            setPaymentMethod(term.payment_method_id);
        }
    };

    const newPaymentTerm = useNewPaymentTerm({
        usage: 'sale',
        created: term => {
            addPaymentTerm(term);
            selectPaymentTerm(term);
        },
    });

    // Transporte: a transportadora vem do cadastro, mas os dados viram
    // snapshot editável — a nota é um documento e o cadastro muda depois.
    const [freightMode, setFreightMode] = useState('0');
    const [carrierId, setCarrierId] = useState(null);
    const [carrier, setCarrier] = useState({
        name: '', document: '', ie: '', street: '', city: '', state: '',
    });
    const [volume, setVolume] = useState({ quantity: '', kind: '', brand: '', number: '' });
    const [vehicle, setVehicle] = useState({ plate: '', state: '' });

    // Item aberto no modal de edição: {index, item}
    const [editingItem, setEditingItem] = useState(null);
    const [classifications, setClassifications] = useState([]);
    // Qual CFOP foi digitado ('modal' ou o índice da linha): a partir daí o
    // campo mostra só o que a linha tem, senão apagar o último dígito traria o
    // da natureza de volta e o próximo dígito entraria emendado nele.
    const [cfopEditing, setCfopEditing] = useState(null);
    const [itemTab, setItemTab] = useState(0);

    // Devolução gerada a partir de uma nota emitida: os itens vêm de lá.
    const [searchParams] = useSearchParams();
    const returnFrom = searchParams.get('return_from');
    const [returnDraft, setReturnDraft] = useState(null);
    const [showReturnItems, setShowReturnItems] = useState(false);

    // Nota existente: pendente abre para editar, emitida abre só para ver.
    const { id } = useParams();
    const [document, setDocument] = useState(null);
    const [loadingDocument, setLoadingDocument] = useState(!!id);
    const readOnly = !!id && document && !document.can_edit;
    const hydratingRef = useRef(false);

    const navigate = useNavigate();

    const isCompany = receiverType === 'PJ';
    const receiverId = isCompany ? selectedCustomerCompany : selectedCustomer;

    const unpaidByOperation = UNPAID_PURPOSES.includes(
        operations.find(operation => operation.id === fiscalOperationId)?.purpose
    );
    const unpaid = paymentMethod === PAYMENT_NONE || unpaidByOperation;

    useEffect(() => {
        if (hydratingRef.current || !unpaidByOperation) return;

        setPaymentMethod(PAYMENT_NONE);
        setPaymentTerm('');
        setPaymentTermId(null);
        setInstallments([]);
    }, [fiscalOperationId, unpaidByOperation]);

    useEffect(() => {
        getIssuers();

        api.get(`/fiscal-operation`).then(res => {
            const resources = res.data.resources ?? [];
            setOperations(resources);
        }).catch(renderError);

        if (id) {
            getDocument();
        }

        if (returnFrom) {
            getReturnDraft();
        }
    }, []);

    /**
     * Devolução a partir da nota emitida: em vez de redigitar tudo, a tela
     * abre já com o cliente, a chave e os itens da origem — o usuário só
     * escolhe o que volta.
     */
    const getReturnDraft = () => {
        api.get(`/fiscal-document/${returnFrom}/return-draft`).then(res => {
            const draft = res.data.resource;

            setReturnDraft(draft);
            hydrateFromReturn(draft);
            setShowReturnItems(true);
        }).catch(error => {
            renderError(error);
            navigate('/fiscal/documents');
        });
    };

    const hydrateFromReturn = draft => {
        hydratingRef.current = true;

        setModel('nfe');
        setSelectedIssuerId(draft.document.company_id);
        setReceiverType(draft.document.receiver_type ?? 'PJ');
        setSelectedCustomer(draft.document.customer_id);
        setSelectedCustomerCompany(draft.document.customer_company_id);
        setReferencedKeys(draft.document.access_key ?? '');
    };

    /** Itens escolhidos no modal viram as linhas da devolução. */
    const applyReturnItems = chosen => {
        setItems(chosen.map(item => ({
            ...emptyItem(),
            description: item.description,
            code: item.code ?? '',
            ncm: item.ncm ?? '',
            cest: item.cest ?? '',
            gtin: item.gtin ?? '',
            unit: item.unit ?? '',
            origin: item.origin ?? '',
            quantity: String(item.quantity),
            unitPrice: convertIntToMoney(item.unit_price),
            // O que amarra a linha à da nota devolvida — preenchido daqui,
            // nunca digitado.
            referenced_item: String(item.number),
            // A devolução espelha a tributação da nota devolvida: os ajustes
            // por linha vêm prontos do backend, com a situação e a alíquota
            // que valeram na venda. Recalcular pela natureza de devolução
            // faria os valores divergirem da original.
            taxes: item.taxes ?? {},
        })));
    };

    /** Reabre a nota salva no mesmo formulário que a criou. */
    const getDocument = () => {
        api.get(`/fiscal-document/${id}`).then(res => {
            const resource = res.data.resource;
            setDocument(resource);

            if (!resource.form_data) {
                // Sem formulário salvo não há o que reabrir. São dois casos,
                // e mandar o usuário para o lugar certo depende de qual:
                // a nota do pedido se consulta no pedido; a nota antiga
                // (anterior a este campo) só existe como PDF/XML.
                if (resource.order_id) {
                    toast.info('Esta nota foi gerada a partir de um pedido — veja os detalhes por lá.');
                    navigate(`/order/edit/${resource.order_id}`);
                    return;
                }

                toast.info('Esta nota foi emitida antes da edição em tela. Use o PDF ou o XML para conferir os dados.');
                navigate('/fiscal/documents');
                return;
            }

            hydrate(stateFromFormData(resource.form_data));
        }).catch(error => {
            renderError(error);
            navigate('/fiscal/documents');
        }).then(() => setLoadingDocument(false));
    };

    const hydrate = state => {
        hydratingRef.current = true;

        setModel(state.model);
        setSelectedIssuerId(state.companyId);
        setReceiverType(state.receiverType);
        setSelectedCustomer(state.customerId);
        setSelectedCustomerCompany(state.customerCompanyId);
        setFiscalOperationId(state.fiscalOperationId);
        setReferencedKeys(state.referencedKeys);
        setPresenceIndicator(state.presenceIndicator);
        setItems(state.items.length > 0 ? state.items : [emptyItem()]);
        setPaymentMethod(state.paymentMethod);
        legacyPaymentCode.current = state.paymentMethodCode;
        setFreight(state.freight);
        setInsurance(state.insurance);
        setOtherExpenses(state.otherExpenses);
        setDiscount(state.discount);
        setAutoTaxes(state.autoTaxes);
        setManualTotals(state.manualTotals);
        setFreightMode(state.freightMode);
        setCarrier(state.carrier);
        setVolume(state.volume);
        setVehicle(state.vehicle);
        setPaymentTerm(state.paymentTerm);
        setPaymentTermId(state.paymentTermId);
        setInstallments(state.installments);
        setInstallmentsEdited(true);
        paymentTouched.current = true;
        setComments(state.comments);
        setServiceDescription(state.serviceDescription);
        setServiceAmount(state.serviceAmount);
        setServiceCode(state.serviceCode);
        setCityServiceCode(state.cityServiceCode);
        setIssRate(state.issRate);
        setIssWithheld(state.issWithheld);
        setWithholdings(state.withholdings);
        // Nota salva com retenção reabre com o bloco aberto — senão o valor
        // continuaria no payload e sumiria da vista.
        setShowWithholdings(
            state.issWithheld
            || Object.values(state.withholdings).some(value => value !== '' && value !== null)
        );
    };

    // Trocar PF/PJ limpa o cliente escolhido — mas reabrir uma nota salva
    // muda o tipo E o cliente juntos, e sem esta trava a limpeza apagaria
    // justamente o cliente que acabou de ser carregado.
    useEffect(() => {
        if (hydratingRef.current) return;

        setSelectedCustomer(null);
        setSelectedCustomerCompany(null);
    }, [receiverType]);

    // Consome a trava no mesmo ciclo em que a hidratação foi aplicada,
    // tenha o efeito acima disparado ou não (efeito sem deps roda a cada
    // render, e depois dos que vêm antes dele).
    useEffect(() => {
        hydratingRef.current = false;
    });

    useEffect(() => {
        setReceiverResource(null);
        setReceiverForm(null);

        if (!receiverId) return;

        setLoadingReceiver(true);

        api.get(isCompany ? `/company/${receiverId}` : `/customer/${receiverId}`).then(res => {
            const resource = res.data.resource;
            setReceiverResource(resource);
            setReceiverForm(receiverFormFrom(resource, isCompany));
        }).catch(renderError).then(() => setLoadingReceiver(false));
    }, [receiverId]);

    const getIssuers = () => {
        setLoadingIssuers(true);

        api.get(`/fiscal-settings`).then(res => {
            const resources = res.data.resources ?? [];
            setIssuers(resources);

            const firstConfigured = resources.find(item => item.setting?.is_configured);
            setSelectedIssuerId((firstConfigured ?? resources[0])?.company?.id ?? null);
        }).catch(renderError).then(() => setLoadingIssuers(false));
    };

    const selectedIssuer = issuers.find(issuer => issuer.company.id === selectedIssuerId);
    const setting = selectedIssuer?.setting;

    // Trocar para um emitente que não emite serviço deixaria a tela numa aba
    // que sumiu, com o formulário de NFS-e no ar.
    useEffect(() => {
        if (model === 'nfse' && setting && !setting.nfse_opt_in) {
            setModel('nfe');
        }
    }, [selectedIssuerId, setting?.nfse_opt_in]);

    // Devolução: a natureza padrão da empresa é de VENDA e sairia com o
    // CFOP errado — assim que as naturezas chegam, troca para uma de
    // devolução (o usuário confere e ajusta, mas não parte do errado).
    useEffect(() => {
        if (!returnFrom || operations.length === 0) return;

        const devolucao = operations.find(operation => operation.purpose === 'return');

        if (devolucao) {
            setFiscalOperationId(devolucao.id);
        }
    }, [returnFrom, operations]);

    // Começa na natureza padrão da empresa (ou na padrão do tenant), sem
    // sobrescrever a escolha que o usuário já fez nesta nota.
    useEffect(() => {
        if (fiscalOperationId || operations.length === 0) return;

        const fallback = operations.find(operation => operation.is_default) ?? operations[0];

        setFiscalOperationId(setting?.default_fiscal_operation_id ?? fallback?.id ?? '');
    }, [selectedIssuerId, operations]);
    const modelEnabled = model === 'nfe' ? !!setting?.nfe_enabled : !!setting?.nfse_enabled;

    // "Emito nota de serviço" é resposta do usuário, não dedução da cidade:
    // quem não marcou não vê a NFS-e aqui. Não mexe no histórico — as notas
    // já emitidas continuam na listagem e na consulta.
    const issuesNfse = !!setting?.nfse_opt_in;

    // Recalcula o CFOP/situação das linhas quando muda algo que a resolução
    // usa. Com atraso: o usuário ainda está digitando NCM e descrição.
    useEffect(() => {
        if (model !== 'nfe' || !fiscalOperationId) {
            setPreview([]);
            return;
        }

        setCalculating(true);
        const request = ++previewRequest.current;

        const timeout = setTimeout(() => {
            api.post(`/fiscal-operation/preview`, {
                fiscal_operation_id: fiscalOperationId,
                company_id: selectedIssuerId,
                receiver_state: receiverForm?.state || null,
                // "ISENTO" não é inscrição: o isento sai como não contribuinte,
                // igual à emissão.
                contributor: isCompany && /\d/.test(receiverForm?.ie ?? ''),
                final_consumer: !isCompany,
                freight: convertMoenyToInt(freight) || 0,
                insurance: convertMoenyToInt(insurance) || 0,
                other_expenses: convertMoenyToInt(otherExpenses) || 0,
                discount: convertMoenyToInt(discount) || 0,
                items: items.map(item => ({
                    ncm: item.ncm || null,
                    origin: item.origin || null,
                    product_id: item.product_id || null,
                    cfop: item.cfop === '' ? null : parseInt(item.cfop, 10),
                    compose_total: item.compose_total !== false,
                    taxes: item.taxes,
                    total: (convertMoenyToInt(item.unitPrice) || 0) * (parseInt(item.quantity, 10) || 0),
                })),
            }).then(res => {
                if (request !== previewRequest.current) return;

                setPreview(res.data.resources ?? []);
                setTotals(res.data.totals ?? null);
                setOperationNotes(res.data.notes ?? { taxpayer: '', fisco: '' });
            }).catch(() => {
                if (request !== previewRequest.current) return;

                setPreview([]);
                setTotals(null);
            }).then(() => {
                if (request === previewRequest.current) setCalculating(false);
            });
        }, 400);

        return () => clearTimeout(timeout);
    }, [
        model, fiscalOperationId, selectedIssuerId, receiverForm?.state, receiverForm?.ie,
        isCompany, items, freight, insurance, otherExpenses, discount,
    ]);

    // A tabela do cClassTrib tem 164 códigos e só serve dentro do modal do
    // item — buscar na montagem da tela cobraria isso de quem nunca o abre.
    useEffect(() => {
        if (!editingItem || classifications.length > 0) return;

        api.get(`/fiscal-operation/tax-classifications`)
            .then(res => setClassifications(res.data.resources ?? []))
            .catch(() => {});
    }, [editingItem]);

    const selectedOperation = operations.find(operation => operation.id === fiscalOperationId);
    const isReturnOperation = selectedOperation?.purpose === 'return';
    const parsedReferencedKeys = referencedKeys
        .split(/[\s,;]+/)
        .map(key => key.replace(/\D/g, ''))
        .filter(key => key.length === 44);

    /**
     * CFOP e situação tributária que a linha vai levar para a nota: o que a
     * natureza resolveu, ou o ajuste da linha quando existe.
     */
    const itemFiscal = index => {
        const tax = preview[index]?.tax ?? (isSimples ? 'simples' : 'icms');
        const override = items[index]?.taxes?.[tax]?.cst;

        return {
            tax,
            cfop: items[index]?.cfop || preview[index]?.cfop || null,
            cst: override || preview[index]?.cst || null,
            overridden: Boolean(override),
        };
    };

    /** Ajuste de um imposto direto na linha (sem abrir o modal). */
    const updateItemTax = (index, tax, changes) => setItems(current => current.map((item, i) => {
        if (i !== index) return item;

        return {
            ...item,
            taxes: {
                ...item.taxes,
                [tax]: { ...(item.taxes?.[tax] ?? emptyTaxRule()), ...changes },
            },
        };
    }));

    const isSimples = isSimplesRegime(setting?.tax_regime);

    // Das duas abas do ICMS só uma vale para o emitente: o Simples
    // destaca CSOSN, o regime normal destaca CST.
    const regimeIcmsTax = isSimples ? 'simples' : 'icms';

    const updateReceiverForm = changes => setReceiverForm(current => ({ ...current, ...changes }));

    // CEP completo preenche endereço, bairro, cidade e UF sozinho (ViaCEP).
    const fillFromZipCode = zipCode => {
        const digits = (zipCode ?? '').replace(/\D/g, '');
        if (digits.length !== 8) return;

        axios.get(`https://viacep.com.br/ws/${digits}/json/`).then(res => {
            const address = res.data;
            if (address.erro) return;

            setReceiverForm(current => ({
                ...current,
                street: address.logradouro || current.street,
                district: address.bairro || current.district,
                city: address.localidade || current.city,
                state: address.uf || current.state,
            }));
        }).catch(() => {});
    };

    const receiverDirty = useMemo(() => {
        if (!receiverResource || !receiverForm) return false;
        const original = receiverFormFrom(receiverResource, isCompany);

        return Object.keys(receiverForm).some(key => (receiverForm[key] ?? '') !== (original[key] ?? ''));
    }, [receiverResource, receiverForm, isCompany]);

    /**
     * Alterações feitas aqui valem para o cadastro — o PUT dos módulos de
     * cliente exige o payload completo, então os campos que a tela não
     * mostra são reenviados como estão no cadastro.
     */
    const saveReceiver = () => {
        if (isCompany) {
            return api.put(`/company/${receiverResource.id}`, {
                trade_name: receiverResource.trade_name,
                company_name: receiverForm.name,
                cnpj: receiverForm.document,
                ie: receiverForm.ie,
                phone: receiverResource.phone,
                type: receiverResource.type,
                is_default_billing_company: receiverResource.is_default_billing_company,
                tax_percentage: receiverResource.tax_percentage,
                can_view_price: receiverResource.can_view_price,
                admin_id: receiverResource.admin_id ?? null,
                zip_code: receiverForm.zip_code,
                street: receiverForm.street,
                number: receiverForm.number,
                complement: receiverForm.complement,
                district: receiverForm.district,
                city: receiverForm.city,
                state: receiverForm.state,
                origin: receiverResource.origin,
                tiny_api_token: receiverResource.tiny_api_token ?? null,
                comments: receiverResource.comments,
            });
        }

        return api.put(`/customer/${receiverResource.id}`, {
            name: receiverForm.name,
            email: receiverForm.email,
            phone: receiverResource.phone,
            cpf: receiverForm.document,
            company_ids: (receiverResource.companies ?? []).map(company => company.id),
            zip_code: receiverForm.zip_code,
            street: receiverForm.street,
            number: receiverForm.number,
            complement: receiverForm.complement,
            district: receiverForm.district,
            city: receiverForm.city,
            state: receiverForm.state,
            type: receiverResource.type,
            admin_id: receiverResource.admin_id ?? null,
            can_view_price: receiverResource.can_view_price,
            comments: receiverResource.comments,
        });
    };

    const updateItem = (index, changes) => {
        setItems(current => current.map((item, i) => i === index ? { ...item, ...changes } : item));
    };

    const serializeItemTaxes = taxes => Object.fromEntries(
        Object.entries(taxes ?? {}).map(([tax, rule]) => {
            const attributes = Object.fromEntries(
                Object.entries(rule.attributes ?? {}).filter(([, value]) => value !== '' && value !== null)
            );

            const clean = {
                cst: rule.cst || null,
                rate: rule.rate === '' || rule.rate === undefined ? null : parseFloat(String(rule.rate).replace(',', '.')),
                base: rule.base === '' || rule.base === undefined ? null : parseFloat(String(rule.base).replace(',', '.')),
                attributes: Object.keys(attributes).length ? attributes : null,
                notes: rule.notes || null,
                fisco_notes: rule.fisco_notes || null,
            };

            return [tax, Object.fromEntries(Object.entries(clean).filter(([, value]) => value !== null))];
        }).filter(([, rule]) => Object.keys(rule).length > 0)
    );

    const updateEditingItem = changes => setEditingItem(current => ({
        ...current,
        item: { ...current.item, ...changes },
    }));

    const updateEditingTax = (tax, kind, key, value) => setEditingItem(current => {
        const rule = current.item.taxes?.[tax] ?? emptyTaxRule();
        const next = kind === 'attribute'
            ? { ...rule, attributes: { ...rule.attributes, [key]: value } }
            : { ...rule, [key]: value };

        return {
            ...current,
            item: { ...current.item, taxes: { ...current.item.taxes, [tax]: next } },
        };
    });

    const removeItem = index => {
        setItems(current => current.filter((_, i) => i !== index));
    };

    // Duplicar é o atalho de quem vende variações do mesmo produto: a
    // tributação ajustada vem junto, que é o trabalhoso de refazer.
    const duplicateItem = index => {
        setItems(current => [
            ...current.slice(0, index + 1),
            { ...current[index], taxes: { ...current[index].taxes } },
            ...current.slice(index + 1),
        ]);
    };

    // Soma pura, sem regra fiscal: dá para calcular na hora, sem esperar o
    // backend. Os gramas do item viram os quilos que a nota leva.
    const weights = useMemo(() => {
        const total = field => items.reduce((sum, item) => {
            const quantity = parseInt(item.quantity, 10) || 0;

            return sum + (parseInt(item[field], 10) || 0) * quantity;
        }, 0);

        return { gross: total('gross_weight') / 1000, net: total('net_weight') / 1000 };
    }, [items]);

    const installmentsTotal = useMemo(
        () => installments.reduce((sum, installment) => sum + (convertMoenyToInt(installment.amount) || 0), 0),
        [installments]
    );

    const productsCents = useMemo(() => {
        if (model === 'nfse') return convertMoenyToInt(serviceAmount) || 0;

        return items.reduce((sum, item) => {
            const quantity = parseInt(item.quantity, 10) || 0;
            return sum + (convertMoenyToInt(item.unitPrice) || 0) * quantity;
        }, 0);
    }, [model, items, serviceAmount]);

    // O que a nota cobra: produtos, frete, seguro e despesas, menos desconto,
    // mais IPI e ICMS-ST. É o valor do pagamento na NF-e, então é ele que as
    // parcelas precisam fechar. A conta é a da prévia do backend; enquanto
    // ela não chega, vale a soma do que a tela já sabe.
    const totalCents = useMemo(() => {
        if (model === 'nfse') return productsCents;

        const manualTotal = !autoTaxes ? convertMoenyToInt(manualTotals.total) || 0 : 0;
        if (manualTotal > 0) return manualTotal;

        if (typeof totals?.total === 'number') return Math.round(totals.total * 100);

        return Math.max(0, productsCents
            + (convertMoenyToInt(freight) || 0)
            + (convertMoenyToInt(insurance) || 0)
            + (convertMoenyToInt(otherExpenses) || 0)
            - (convertMoenyToInt(discount) || 0));
    }, [model, productsCents, autoTaxes, manualTotals.total, totals, freight, insurance, otherExpenses, discount]);

    // Quem calcula valores e vencimentos é o backend, com a mesma regra dos
    // títulos do pedido; os dias contam da emissão, que é hoje.
    const generateInstallments = termId => {
        const request = ++installmentsRequest.current;

        api.post(`/fiscal-document/installments`, {
            payment_term_id: termId,
            total: totalCents,
        }).then(res => {
            if (request !== installmentsRequest.current) return;

            setInstallments(res.data.resource.installments.map(installment => ({
                due_date: installment.due_date,
                amount: convertIntToMoney(installment.amount),
            })));
        }).catch(renderError);
    };

    useEffect(() => {
        if (!paymentTermId || installmentsEdited || unpaid) return;

        const timeout = setTimeout(() => generateInstallments(paymentTermId), 300);
        return () => clearTimeout(timeout);
    }, [paymentTermId, totalCents, installmentsEdited, unpaid]);

    // Nota nova: começa com a condição e a forma padrão do tenant e troca
    // pelas do cliente quando ele é escolhido — até o usuário mexer.
    useEffect(() => {
        if (id || returnFrom || paymentTouched.current) return;

        api.get(`/payment-term/defaults`, {
            params: { company_id: isCompany ? selectedCustomerCompany : undefined, usage: 'sale' },
        }).then(res => {
            if (paymentTouched.current) return;

            const { term, method } = res.data.resource;

            if (method) {
                setPaymentMethod(current => (current === PAYMENT_NONE ? current : method.id));
            }
            if (term) {
                setPaymentTermId(term.id);
                setPaymentTerm(term.name);
            }
        }).catch(() => null);
    }, [selectedCustomerCompany]);

    useEffect(() => {
        if (paymentMethod || !legacyPaymentCode.current || paymentMethods.length === 0) return;

        const match = paymentMethods.find(method => method.sefaz_code === legacyPaymentCode.current);
        if (match) setPaymentMethod(match.id);
        legacyPaymentCode.current = null;
    }, [paymentMethods, paymentMethod]);

    const receiverReady = receiverForm
        && receiverForm.document.trim() !== ''
        && receiverForm.zip_code.trim() !== ''
        && receiverForm.number.trim() !== ''
        && receiverForm.district.trim() !== ''
        && receiverForm.city.trim() !== ''
        && receiverForm.state.trim() !== '';

    // Transportador com IE precisa da UF (campo X10 da NF-e).
    const carrierNeedsState = model === 'nfe' && freightMode !== '9' && carrier.ie.trim() !== '' && !carrier.state;

    const canIssue = !issuing
        && !carrierNeedsState
        && setting?.is_configured
        && modelEnabled
        && receiverReady
        && productsCents > 0
        && (model === 'nfse' || !isReturnOperation || parsedReferencedKeys.length > 0)
        && (model === 'nfse'
            ? serviceDescription.trim() !== '' && serviceCode !== '' && issRate !== ''
            : items.every(item => item.description.trim() !== '' && (parseInt(item.quantity, 10) || 0) > 0));

    const issue = async () => {
        setIssuing(true);

        try {
            // Corrigiu o cadastro aqui? Salva antes — a nota é montada com o
            // que está gravado no cliente.
            if (receiverDirty) {
                await saveReceiver();
            }

            const payload = {
                model,
                company_id: selectedIssuerId,
                receiver_type: receiverType,
                customer_id: !isCompany ? selectedCustomer : null,
                customer_company_id: isCompany ? selectedCustomerCompany : null,
            };

            if (model === 'nfe') {
                // A natureza define CFOP e tributação da nota.
                payload.fiscal_operation_id = fiscalOperationId || null;
                payload.referenced_keys = parsedReferencedKeys;
                payload.presence_indicator = presenceIndicator;
                payload.items = items.map(item => ({
                    description: item.description.trim(),
                    ncm: item.ncm.trim() || null,
                    quantity: parseInt(item.quantity, 10) || 0,
                    unit_price: convertMoenyToInt(item.unitPrice) || 0,
                    product_id: item.product_id || null,
                    code: item.code?.trim() || null,
                    unit: item.unit?.trim() || null,
                    cest: item.cest?.trim() || null,
                    gtin: item.gtin?.trim() || null,
                    origin: item.origin || null,
                    additional_info: item.additional_info?.trim() || null,
                    cfop: item.cfop === '' ? null : parseInt(item.cfop, 10),
                    compose_total: item.compose_total !== false,
                    gross_weight: item.gross_weight === '' ? null : parseInt(item.gross_weight, 10),
                    net_weight: item.net_weight === '' ? null : parseInt(item.net_weight, 10),
                    // Devolução por item: sem chave própria, vale a do
                    // cabeçalho (o backend resolve).
                    referenced_item: item.referenced_item === '' ? null : parseInt(item.referenced_item, 10),
                    referenced_key: item.referenced_key || null,
                    // Só vai o que a linha realmente ajustou: campo vazio
                    // significa "continua valendo a natureza".
                    taxes: serializeItemTaxes(item.taxes),
                }));
                // O meio da NF-e sai da forma cadastrada, no backend. Sem
                // pagamento não leva prazo nem duplicata, mesmo que tenham
                // sobrado da natureza escolhida antes.
                payload.payment_method = unpaid ? PAYMENT_NONE : null;
                payload.payment_method_id = unpaid ? null : paymentMethod;
                payload.payment_term_id = unpaid ? null : paymentTermId;
                payload.payment_term = unpaid ? null : (paymentTerm.trim() || null);
                payload.installments = unpaid ? [] : installments
                    .filter(installment => installment.due_date && convertMoenyToInt(installment.amount))
                    .map(installment => ({
                        due_date: installment.due_date,
                        amount: convertMoenyToInt(installment.amount),
                    }));
                payload.freight = convertMoenyToInt(freight) || 0;
                payload.insurance = convertMoenyToInt(insurance) || 0;
                payload.other_expenses = convertMoenyToInt(otherExpenses) || 0;
                payload.discount = convertMoenyToInt(discount) || 0;
                payload.auto_tax_calculation = autoTaxes;
                payload.freight_mode = freightMode;
                payload.comments = comments.trim() || null;

                if (freightMode !== '9') {
                    payload.carrier_name = carrier.name.trim() || null;
                    payload.carrier_document = carrier.document.trim() || null;
                    payload.carrier_ie = carrier.ie.trim() || null;
                    payload.carrier_street = carrier.street.trim() || null;
                    payload.carrier_city = carrier.city.trim() || null;
                    payload.carrier_state = carrier.state || null;
                    payload.volume_quantity = volume.quantity === '' ? null : parseInt(volume.quantity, 10);
                    payload.volume_kind = volume.kind.trim() || null;
                    payload.volume_brand = volume.brand.trim() || null;
                    payload.volume_number = volume.number.trim() || null;
                    payload.vehicle_plate = vehicle.plate.trim() || null;
                    payload.vehicle_state = vehicle.state || null;
                }

                // Com o cálculo desligado, os totais vão como estão na tela
                // (o que o usuário digitou ou, se não mexeu, o calculado).
                if (!autoTaxes) {
                    payload.totals = Object.fromEntries(
                        TOTAL_KEYS.map(key => [key, convertMoenyToInt(totalValue(key)) || 0])
                    );
                }
            } else {
                payload.service_description = serviceDescription.trim();
                payload.amount = convertMoenyToInt(serviceAmount) || 0;
                payload.service_code = serviceCode || null;
                payload.city_service_code = cityServiceCode.trim() || null;
                payload.iss_rate = issRate === '' ? null : parseFloat(String(issRate).replace(',', '.'));
                payload.iss_withheld = issWithheld;
                payload.withholdings = {
                    ...Object.fromEntries(WITHHOLDING_FIELDS.map(field => [
                        field.key,
                        withholdings[field.key] === ''
                            ? null
                            : parseFloat(String(withholdings[field.key]).replace(',', '.')),
                    ])),
                    others: convertMoenyToInt(withholdings.others) || null,
                };
            }

            // Salvar não é emitir: a nota fica pendente e sai quando o
            // usuário mandar, pelas ações da lista.
            id
                ? await api.put(`/fiscal-document/${id}`, payload)
                : await api.post(`/fiscal-document/issue`, payload);

            toast.success(id
                ? 'Nota atualizada. Ela continua pendente até você emitir.'
                : 'Nota salva e pendente de emissão — use "Emitir nota fiscal" na lista quando quiser enviá-la ao fisco'
            );
            navigate('/fiscal/documents');
        } catch (error) {
            renderError(error);
        }

        setIssuing(false);
    };

    const renderReceiverFields = () => {
        if (loadingReceiver) {
            return <p className={`fiscal-issue-hint`}>Carregando dados do cliente...</p>;
        }

        if (!receiverForm) return null;

        // Cliente cadastrado na hora (pelo "Cadastrar novo") costuma chegar
        // sem documento ou endereço, e a nota só é recusada lá no envio.
        const missing = [
            ['document', isCompany ? 'CNPJ' : 'CPF'],
            ['zip_code', 'CEP'],
            ['street', 'endereço'],
            ['number', 'número'],
            ['district', 'bairro'],
            ['city', 'cidade'],
            ['state', 'estado'],
        ].filter(([field]) => !String(receiverForm[field] ?? '').trim()).map(([, label]) => label);

        return (
            <>
                <div className="row">
                    <div className="col-6">
                        <Input
                            label={isCompany ? `Razão social` : `Nome completo`}
                            value={receiverForm.name}
                            change={value => updateReceiverForm({ name: value })}
                        />
                    </div>
                    <div className="col-3">
                        <Input
                            label={isCompany ? `CNPJ` : `CPF`}
                            value={receiverForm.document}
                            change={value => updateReceiverForm({ document: value })}
                            mask={isCompany ? `convertToCnpj` : `convertToCpf`}
                        />
                    </div>
                    <div className="col-3">
                        {isCompany
                            ? <Input
                                label={`Inscrição estadual`}
                                value={receiverForm.ie}
                                change={value => updateReceiverForm({ ie: value })}
                            />
                            : <Input
                                label={`E-mail`}
                                value={receiverForm.email}
                                change={value => updateReceiverForm({ email: value })}
                            />
                        }
                    </div>
                </div>
                <div className="row">
                    <div className="col-3">
                        <Input
                            label={`CEP`}
                            value={receiverForm.zip_code}
                            change={value => {
                                updateReceiverForm({ zip_code: value });
                                fillFromZipCode(value);
                            }}
                            mask={`convertToCep`}
                        />
                    </div>
                    <div className="col-6">
                        <Input
                            label={`Endereço`}
                            value={receiverForm.street}
                            change={value => updateReceiverForm({ street: value })}
                        />
                    </div>
                    <div className="col-3">
                        <Input
                            label={`Número`}
                            value={receiverForm.number}
                            change={value => updateReceiverForm({ number: value })}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-3">
                        <Input
                            label={`Complemento`}
                            value={receiverForm.complement}
                            change={value => updateReceiverForm({ complement: value })}
                        />
                    </div>
                    <div className="col-3">
                        <Input
                            label={`Bairro`}
                            value={receiverForm.district}
                            change={value => updateReceiverForm({ district: value })}
                        />
                    </div>
                    <div className="col-3">
                        <Input
                            label={`Cidade`}
                            value={receiverForm.city}
                            change={value => updateReceiverForm({ city: value })}
                        />
                    </div>
                    <div className="col-3">
                        <InputSelect
                            menuInBody
                            label={`Estado`}
                            options={states}
                            value={receiverForm.state}
                            change={value => updateReceiverForm({ state: value })}
                        />
                    </div>
                </div>
                {missing.length > 0 &&
                    <p className={`fiscal-issue-hint`}>
                        <Icon name={`alert`} /> Falta no cadastro do cliente: {missing.join(', ')}.
                        Preencha aqui antes de emitir.
                    </p>
                }
                {receiverDirty &&
                    <p className={`fiscal-issue-hint`}>
                        <Icon name={`alert`} /> Os dados alterados serão salvos no cadastro
                        do cliente ao emitir a nota.
                    </p>
                }
            </>
        );
    };

    if (!loadingIssuers && !issuers.some(issuer => issuer.setting?.is_configured)) {
        return (
            <PanelTemplate id="fiscal-issue">
                <PageHeader
                    left={<h2 className={`fiscal-title`}>Nova nota fiscal</h2>}
                    right={<Button type={`secondary`} size={`small`} text={`Voltar para as notas`} link={`/fiscal/documents`} />}
                />
                <Box>
                    <div className={`fiscal-issue-empty`}>
                        <p>
                            Antes de emitir, é preciso configurar a emissão fiscal: dados da
                            empresa, impostos e certificado digital.
                        </p>
                        <Button
                            type={`primary`}
                            size={`small`}
                            text={`Ir para as configurações fiscais`}
                            action={() => navigate('/fiscal/settings')}
                        />
                    </div>
                </Box>
            </PanelTemplate>
        );
    }

    const changePaymentMethod = value => {
        paymentTouched.current = true;
        setPaymentMethod(value);
    };

    const changePaymentTerm = value => {
        const term = paymentTerms.find(item => item.id === value);
        if (term) selectPaymentTerm(term);
    };

    const editInstallments = update => {
        setInstallmentsEdited(true);
        setInstallments(update);
    };

    // Sem total ainda, as parcelas da condição sairiam todas zeradas.
    const awaitingTotal = totalCents === 0 && !installmentsEdited;

    const addInstallment = () => editInstallments(current => [
        ...current,
        { due_date: addDaysTo(current[current.length - 1]?.due_date, current.length ? 30 : 0), amount: '' },
    ]);

    const updateInstallment = (index, changes) => editInstallments(current =>
        current.map((installment, i) => i === index ? { ...installment, ...changes } : installment)
    );

    const paymentMethodOptions = [
        ...paymentMethods.map(method => ({ label: method.name, value: method.id })),
        { label: 'Sem pagamento', value: PAYMENT_NONE },
    ];

    // Nota salva antes do cadastro de condições fica com o texto dela.
    const paymentTermOptions = [
        ...paymentTerms.map(term => ({ label: term.name, value: term.id })),
        ...(paymentTermId && !paymentTerms.some(term => term.id === paymentTermId)
            ? [{ label: `${paymentTerm} (inativa)`, value: paymentTermId }]
            : []),
        ...(!paymentTermId && paymentTerm ? [{ label: `"${paymentTerm}" (texto livre)`, value: 'legacy' }] : []),
    ];

    const updateCarrier = changes => setCarrier(current => ({ ...current, ...changes }));

    /** Preenche o transportador com o cadastro escolhido, deixando editável. */
    const fillCarrier = resource => {
        if (!resource) {
            setCarrier({ name: '', document: '', ie: '', street: '', city: '', state: '' });
            return;
        }

        setCarrier({
            name: resource.trade_name ?? resource.company_name ?? resource.name ?? '',
            document: resource.cnpj ?? resource.cpf ?? '',
            ie: resource.ie ?? '',
            street: [resource.street, resource.number].filter(Boolean).join(', '),
            city: resource.city ?? '',
            state: resource.state ?? '',
        });
    };

    /**
     * Valor de um total: o calculado, ou o que o usuário digitou quando o
     * cálculo automático está desligado.
     */
    const totalValue = key => autoTaxes
        ? convertIntToMoney(Math.round((totals?.[key] ?? 0) * 100))
        : (manualTotals[key] ?? convertIntToMoney(Math.round((totals?.[key] ?? 0) * 100)));

    const totalField = (key, label) => (
        <div className={`col-3 totals-calculated ${calculating && autoTaxes ? 'stale' : ''}`}>
            <Input
                label={label}
                value={totalValue(key)}
                change={value => setManualTotals(current => ({ ...current, [key]: value }))}
                mask={`convertToMoney`}
                disabled={autoTaxes}
            />
        </div>
    );

    /**
     * CFOP da linha em edição. Mesma regra dos impostos: o campo mostra o que
     * a nota vai levar — o da linha quando existe, senão o da natureza — e só
     * o que for digitado vira ajuste.
     */
    const editingCfop = {
        own: editingItem?.item?.cfop ?? '',
        inherited: String(preview[editingItem?.index]?.inherited_cfop ?? ''),
    };

    const taxTabs = itemTaxTabs(isSimples);

    /** Aba do modal do item — uma aba pode reunir mais de um imposto. */
    const itemTaxTab = tab => (
        <div className="item-tax-tab">
            {/* A origem fica aqui, e não entre os campos opcionais: ela é
                campo fixo do grupo do ICMS em toda NF-e e ainda escolhe a
                exceção da natureza — trocá-la muda a situação tributária. */}
            {tab.taxes.some(item => item.key === regimeIcmsTax) &&
                <div className="row">
                    <div className="col-4">
                        <InputSelect
                            menuInBody
                            label={`Origem da mercadoria`}
                            options={FISCAL_ORIGINS}
                            value={editingItem?.item?.origin || '0'}
                            change={value => updateEditingItem({ origin: value })}
                        />
                    </div>
                </div>
            }
            {tab.taxes.map(({ key, title }) => (
                <div key={key} className={title ? 'section' : undefined}>
                    {title && <h4>{title}</h4>}
                    <TaxRuleFields
                        tax={key}
                        rule={editingItem?.item?.taxes?.[key] ?? emptyTaxRule()}
                        inherited={preview[editingItem?.index]?.inherited?.[key] ?? {}}
                        classifications={classifications}
                        direction={selectedOperation?.direction ?? 'out'}
                        onChange={(kind, field, value) => updateEditingTax(key, kind, field, value)}
                    />
                </div>
            ))}
        </div>
    );

    if (loadingDocument) {
        return (
            <PanelTemplate id="fiscal-issue">
                <PageHeader left={<h2 className={`fiscal-title`}>Nota fiscal</h2>} />
                <Box><p className={`fiscal-issue-hint`}>Carregando nota...</p></Box>
            </PanelTemplate>
        );
    }

    return (
        <PanelTemplate id="fiscal-issue">
            <Modal
                title={`Item da nota`}
                size={`big`}
                show={editingItem !== null}
                close={() => setEditingItem(null)}
                footer={
                    <Button
                        type={`primary`}
                        size={`small`}
                        svg={`save-white`}
                        text={`Aplicar`}
                        action={() => {
                            updateItem(editingItem.index, editingItem.item);
                            setEditingItem(null);
                        }}
                    />
                }
            >
                {editingItem &&
                    <BoxTabs
                        className="item-edit-tabs"
                        tab={itemTab}
                        setTab={setItemTab}
                        titles={['Dados do item', ...taxTabs.map(item => item.label)]}
                        contents={[
                            <div className="section">
                                <div className="row">
                                    <div className="col-8">
                                        <Input
                                            label={`Descrição`}
                                            value={editingItem.item.description}
                                            change={value => updateEditingItem({ description: value })}
                                        />
                                    </div>
                                    <div className="col-4">
                                        <Input
                                            label={`Código (SKU)`}
                                            value={editingItem.item.code}
                                            change={value => updateEditingItem({ code: value })}
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-4">
                                        <Input
                                            label={`NCM`}
                                            value={editingItem.item.ncm}
                                            change={value => updateEditingItem({ ncm: value })}
                                            mask={`convertToNCM`}
                                        />
                                    </div>
                                    <div className={editingCfop.own !== '' ? 'col-4 tax-field adjusted' : 'col-4'}>
                                        <Input
                                            label={`CFOP`}
                                            value={formatCfop(cfopEditing === 'modal'
                                                ? editingCfop.own
                                                : editingCfop.own || editingCfop.inherited
                                            )}
                                            change={value => {
                                                setCfopEditing('modal');
                                                updateEditingItem({
                                                    cfop: (value ?? '').replace(/\D/g, '').slice(0, 4),
                                                });
                                            }}
                                            onFocus={event => event.target.select()}
                                            onBlur={() => setCfopEditing(null)}
                                        />
                                        {editingCfop.own !== '' && editingCfop.inherited !== '' &&
                                            editingCfop.own !== editingCfop.inherited &&
                                            <p className="input-hint tax-field-note">
                                                Ajustado — a natureza define {formatCfop(editingCfop.inherited)}
                                            </p>
                                        }
                                    </div>
                                    <div className="col-4">
                                        <Input
                                            label={`Unidade`}
                                            value={editingItem.item.unit}
                                            // uCom da NF-e: até 6 caracteres.
                                            change={value => updateEditingItem({ unit: value.toUpperCase().slice(0, 6) })}
                                            placeholder={setting?.default_fiscal_unit ?? 'UN'}
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-4">
                                        <Input
                                            type={`number`}
                                            label={`Quantidade`}
                                            value={editingItem.item.quantity}
                                            change={value => updateEditingItem({ quantity: value })}
                                        />
                                    </div>
                                    <div className="col-4">
                                        <Input
                                            label={`Valor unitário`}
                                            value={editingItem.item.unitPrice}
                                            change={value => updateEditingItem({ unitPrice: value })}
                                            mask={`convertToMoney`}
                                        />
                                    </div>
                                    <div className="col-4">
                                        <Input
                                            label={`Valor total`}
                                            value={convertIntToMoney(
                                                (convertMoenyToInt(editingItem.item.unitPrice) || 0)
                                                * (parseInt(editingItem.item.quantity, 10) || 0),
                                                true
                                            )}
                                            change={() => {}}
                                            disabled
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-6">
                                        <Input
                                            label={`GTIN / EAN`}
                                            value={editingItem.item.gtin}
                                            change={value => updateEditingItem({ gtin: value })}
                                        />
                                    </div>
                                    <div className="col-6">
                                        <Input
                                            label={`CEST`}
                                            value={editingItem.item.cest}
                                            change={value => updateEditingItem({ cest: value.replace(/\D/g, '').slice(0, 7) })}
                                            placeholder={`7 dígitos`}
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-4">
                                        <Input
                                            label={`Peso bruto (g)`}
                                            value={editingItem.item.gross_weight}
                                            change={value => updateEditingItem({
                                                gross_weight: (value ?? '').replace(/\D/g, ''),
                                            })}
                                        />
                                        <p className={`fiscal-issue-hint`}>Por unidade — a nota soma pela quantidade</p>
                                    </div>
                                    <div className="col-4">
                                        <Input
                                            label={`Peso líquido (g)`}
                                            value={editingItem.item.net_weight}
                                            change={value => updateEditingItem({
                                                net_weight: (value ?? '').replace(/\D/g, ''),
                                            })}
                                        />
                                    </div>
                                </div>
                                <div className="toggle-row">
                                    <Toggle
                                        checked={editingItem.item.compose_total !== false}
                                        onChange={value => updateEditingItem({ compose_total: value })}
                                    />
                                    <div>
                                        <strong>Item compõe o valor total da nota</strong>
                                        <p>Desligue quando a mercadoria acompanha a nota mas não é cobrada</p>
                                    </div>
                                </div>
                                <Input
                                    label={`Informações adicionais`}
                                    value={editingItem.item.additional_info}
                                    change={value => updateEditingItem({ additional_info: value })}
                                />

                                {/* O número do item fica na LINHA (é o dado
                                    que todo mundo preenche); aqui só o caso
                                    raro de uma linha vir de outra nota. */}
                                {isReturnOperation &&
                                    <div className="section">
                                        <h4>Item devolvido</h4>
                                        <Input
                                            label={`Chave da nota de origem`}
                                            value={editingItem.item.referenced_key}
                                            change={value => updateEditingItem({
                                                referenced_key: (value ?? '').replace(/\D/g, ''),
                                            })}
                                        />
                                        <p className="input-hint">
                                            Vazio usa a chave informada na nota — preencha só se esta
                                            linha vier de outra nota
                                        </p>
                                    </div>
                                }
                            </div>,
                            ...taxTabs.map(tab => itemTaxTab(tab)),
                        ]}
                    />
                }
            </Modal>

            <PageHeader
                left={
                    <h2 className={`fiscal-title`}>
                        {!id ? 'Nova nota fiscal' : (readOnly ? 'Nota fiscal' : 'Editar nota fiscal')}
                    </h2>
                }
                right={<Button type={`secondary`} size={`small`} text={`Voltar para as notas`} link={`/fiscal/documents`} />}
            />

            <ModalReturnItems
                show={showReturnItems}
                close={() => setShowReturnItems(false)}
                draft={returnDraft}
                onConfirm={applyReturnItems}
            />

            {document?.order &&
                <div className={`fiscal-alert`}>
                    <Icon name={`alert`} />
                    <span>
                        Nota gerada a partir do pedido <strong>#{document.order.code}</strong>. O que
                        você alterar aqui vale só para a nota — o pedido continua como está.
                    </span>
                    <button onClick={() => navigate(`/order/edit/${document.order_id}`)}>Ver pedido</button>
                </div>
            }

            {/* De onde esta devolução veio — e o caminho de volta para
                escolher outros itens sem recomeçar. */}
            {returnDraft &&
                <div className={`fiscal-alert`}>
                    <Icon name={`alert`} />
                    <span>
                        Devolvendo a nota {returnDraft.document.number ? <strong>nº {returnDraft.document.number}</strong> : 'de origem'}
                        {returnDraft.document.receiver_name ? ` de ${returnDraft.document.receiver_name}` : ''}.
                        Os itens vêm com a <strong>tributação da nota devolvida</strong> — situação e
                        alíquota espelhadas, para os valores não divergirem da original. Confira a
                        natureza de operação antes de salvar.
                    </span>
                    <button onClick={() => setShowReturnItems(true)}>Escolher itens</button>
                </div>
            }

            {/* Por que a nota não foi adiante — com a ação que resolve.
                Antes da lista e desta tela mostrarem isto, o motivo só
                existia na aba de notas do pedido, fora do alcance de quem
                emitiu nota avulsa. */}
            <DocumentProblem problem={document?.friendly_problem} />

            {/* Nota já enviada não se altera: a tela vira consulta, sem
                botão de salvar e sem campo clicável. */}
            {readOnly &&
                <div className={`fiscal-alert`}>
                    <Icon name={`alert`} />
                    <span>
                        Esta nota já foi enviada ao fisco e não pode ser alterada. Você está
                        vendo os dados como eles foram transmitidos.
                    </span>
                </div>
            }

            <div className={readOnly ? `fiscal-issue-locked` : ''}>

            <Box>
                <span className={`fiscal-issue-section`}>O que você vai emitir</span>
                <div className={`fiscal-issue-types`}>
                    <button
                        className={`issue-type ${model === 'nfe' ? 'active' : ''}`}
                        onClick={() => setModel('nfe')}
                    >
                        <strong>NF-e</strong>
                        <span>Nota de produto</span>
                    </button>
                    {issuesNfse &&
                        <button
                            className={`issue-type ${model === 'nfse' ? 'active' : ''}`}
                            onClick={() => setModel('nfse')}
                        >
                            <strong>NFS-e</strong>
                            <span>Nota de serviço</span>
                        </button>
                    }
                </div>

                {!modelEnabled && setting?.is_configured &&
                    <p className={`fiscal-issue-hint warning`}>
                        A emissão de {model === 'nfe' ? 'NF-e' : 'NFS-e'} está desligada para esta
                        empresa — veja o que falta em{' '}
                        <button className={`link`} onClick={() => navigate('/fiscal/settings')}>
                            Configurações fiscais
                        </button>.
                    </p>
                }

                {issuers.length > 1 &&
                    <div className="row">
                        <div className="col-6">
                            <InputSelect
                                menuInBody
                                label={`Empresa emitente`}
                                options={createOptions(issuers.map(issuer => issuer.company), ['trade_name', 'cnpj'], 'id')}
                                value={selectedIssuerId}
                                change={setSelectedIssuerId}
                            />
                        </div>
                    </div>
                }
            </Box>

            <Box>
                <span className={`fiscal-issue-section`}>Cliente</span>
                <div className="row">
                    <div className="col-3">
                        <InputSelect
                            menuInBody
                            label={`Tipo de cliente`}
                            options={RECEIVER_TYPES}
                            value={receiverType}
                            change={setReceiverType}
                        />
                    </div>
                    <div className="col-9">
                        {isCompany
                            ? <SelectCustomerCompany
                                selectedCustomerCompany={selectedCustomerCompany}
                                setSelectedCustomerCompany={setSelectedCustomerCompany}
                                create={true}
                            />
                            : <SelectCustomer
                                selectedCustomer={selectedCustomer}
                                setSelectedCustomer={setSelectedCustomer}
                                create={{ type: ['customer'] }}
                            />
                        }
                    </div>
                </div>

                {!receiverId &&
                    <p className={`fiscal-issue-hint`}>
                        Os dados do cliente saem do cadastro e aparecem aqui para conferência —
                        dá para corrigir na hora, sem trocar de tela.
                    </p>
                }

                {renderReceiverFields()}
            </Box>

            {model === 'nfe' &&
                <Box>
                    <span className={`fiscal-issue-section`}>Natureza da operação</span>
                    <div className="row">
                        <div className="col-6">
                            <InputSelect
                                label={`O que esta nota está fazendo`}
                                options={operations.map(operation => ({ label: operation.name, value: operation.id }))}
                                value={fiscalOperationId}
                                change={setFiscalOperationId}
                            />
                            <p className={`fiscal-issue-hint`}>
                                Define o CFOP e a tributação da nota
                            </p>
                        </div>
                        <div className="col-6">
                            <InputSelect
                                label={`Presença do comprador`}
                                options={PRESENCE_INDICATOR_OPTIONS}
                                value={presenceIndicator}
                                change={setPresenceIndicator}
                            />
                        </div>
                        {/* Devolução sem a chave da NF-e original é recusada
                            pela SEFAZ (rejeição 321). */}
                        {isReturnOperation &&
                            <div className="col-6">
                                <Input
                                    label={`Chave da NF-e original *`}
                                    value={referencedKeys}
                                    change={setReferencedKeys}
                                />
                                <p className={`fiscal-issue-hint`}>
                                    44 dígitos, impressos na DANFE. Mais de uma chave? Separe por vírgula.
                                </p>
                            </div>
                        }
                    </div>
                </Box>
            }

            {model === 'nfe' &&
                <Box>
                    <span className={`fiscal-issue-section`}>Itens da nota</span>

                    {isReturnOperation &&
                        <p className={`fiscal-issue-hint`}>
                            Devolução: informe em <strong>Item de origem</strong> a posição de cada
                            produto na nota devolvida.
                        </p>
                    }

                    <div className={`fiscal-issue-items with-fiscal ${isReturnOperation ? 'with-return' : ''}`}>
                        <div className={`items-head`}>
                            {isReturnOperation && <span>Item origem</span>}
                            <span>Descrição</span>
                            <span>NCM</span>
                            <span>CFOP</span>
                            <span>Sit. tributária</span>
                            <span>Qtd.</span>
                            <span>Valor unitário</span>
                            <span></span>
                        </div>
                        {items.map((item, index) => {
                            const fiscal = itemFiscal(index);

                            return (
                                <div className={`items-row`} key={index}>
                                    {isReturnOperation &&
                                        <div className={`items-return ${item.referenced_item === '' ? 'missing' : ''}`}>
                                            <Input
                                                value={item.referenced_item}
                                                change={value => updateItem(index, {
                                                    referenced_item: (value ?? '').replace(/\D/g, ''),
                                                })}
                                                placeholder={`nº`}
                                            />
                                        </div>
                                    }
                                    <ItemDescription
                                        value={item.description}
                                        onPick={product => updateItem(index, itemFromProduct(item, product))}
                                        onType={value => updateItem(index, { description: value, product_id: null })}
                                    />
                                    <Input
                                        value={item.ncm}
                                        change={value => updateItem(index, { ncm: value })}
                                        mask={`convertToNCM`}
                                        placeholder={`8 dígitos`}
                                    />
                                    {/* Vêm preenchidos pela natureza, mas são
                                        editáveis: o campo mostra o que a
                                        natureza resolveu até alguém trocar, e
                                        aí a linha passa a mandar (destaque). */}
                                    <div className={`items-fiscal ${item.cfop ? 'adjusted' : ''}`}>
                                        <Input
                                            value={formatCfop(cfopEditing === index ? item.cfop : fiscal.cfop)}
                                            change={value => {
                                                setCfopEditing(index);
                                                updateItem(index, {
                                                    cfop: (value ?? '').replace(/\D/g, '').slice(0, 4),
                                                });
                                            }}
                                            onFocus={event => event.target.select()}
                                            onBlur={() => setCfopEditing(null)}
                                            placeholder={`CFOP`}
                                        />
                                    </div>
                                    <div className={`items-fiscal ${fiscal.overridden ? 'adjusted' : ''}`}>
                                        <InputSelect
                                            menuInBody
                                            removeMargin
                                            options={isSimples ? CSOSN_OPTIONS : CST_OPTIONS}
                                            value={fiscal.cst ?? ''}
                                            change={value => updateItemTax(index, fiscal.tax, { cst: value })}
                                        />
                                    </div>
                                    <Input
                                        type={`number`}
                                        value={item.quantity}
                                        change={value => updateItem(index, { quantity: value })}
                                    />
                                    <Input
                                        value={item.unitPrice}
                                        change={value => updateItem(index, { unitPrice: value })}
                                        mask={`convertToMoney`}
                                    />
                                    <div className={`items-actions`}>
                                        <button
                                            onClick={() => { setEditingItem({ index, item }); setItemTab(0); }}
                                            title={`Editar item`}
                                        >
                                            <Icon name={`edit`} />
                                        </button>
                                        <button
                                            onClick={() => duplicateItem(index)}
                                            title={`Duplicar item`}
                                        >
                                            <Icon name={`copy`} />
                                        </button>
                                        <button
                                            className={`remove`}
                                            onClick={() => removeItem(index)}
                                            disabled={items.length === 1}
                                            title={`Remover item`}
                                        >
                                            <Icon name={`delete`} />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <Button
                        type={`secondary`}
                        size={`small`}
                        text={`Adicionar item`}
                        action={() => setItems(current => [...current, emptyItem()])}
                    />
                </Box>
            }

            {/* Cálculo de impostos e pagamento são assuntos próprios: cada um
                na sua caixa, e não apêndices da lista de itens. */}
            {model === 'nfe' &&
                <Box>
                    <div className={`fiscal-issue-totals`}>
                        <div className={`totals-head`}>
                            <span className={`fiscal-issue-section`}>
                                Cálculo de impostos
                                {/* Sempre no DOM, só invisível: aparecer e
                                    sumir mudava a altura da linha e empurrava
                                    os campos. */}
                                <span
                                    className={`totals-calculating ${calculating && autoTaxes ? 'visible' : ''}`}
                                    aria-live="polite"
                                >
                                    <span className={`dot`} />
                                    Recalculando...
                                </span>
                            </span>
                            <div className={`totals-auto`}>
                                <Toggle checked={autoTaxes} onChange={setAutoTaxes} />
                                <span>{autoTaxes ? 'Cálculo automático ligado' : 'Cálculo automático desligado'}</span>
                            </div>
                        </div>

                        {!autoTaxes &&
                            <p className={`fiscal-issue-hint`}>
                                Os impostos vão para a nota exatamente como você digitar aqui — o
                                sistema para de calcular.
                            </p>
                        }

                        {/* A primeira linha é o que quase toda nota precisa;
                            o resto só aparece a pedido. */}
                        <div className="row">
                            <div className={`col-3 totals-calculated ${calculating && autoTaxes ? 'stale' : ''}`}>
                                <Input
                                    label={`Total dos produtos`}
                                    value={convertIntToMoney(Math.round((totals?.products ?? 0) * 100))}
                                    change={() => {}}
                                    disabled
                                />
                            </div>
                            <div className="col-3">
                                <Input
                                    label={`Frete`}
                                    value={freight}
                                    change={setFreight}
                                    mask={`convertToMoney`}
                                />
                            </div>
                            <div className="col-3">
                                <Input
                                    label={`Desconto`}
                                    value={discount}
                                    change={setDiscount}
                                    mask={`convertToMoney`}
                                />
                            </div>
                            <div className={`col-3 totals-calculated ${calculating && autoTaxes ? 'stale' : ''}`}>
                                <Input
                                    label={`Total da nota`}
                                    value={totalValue('total')}
                                    change={value => setManualTotals(current => ({ ...current, total: value }))}
                                    mask={`convertToMoney`}
                                    disabled={autoTaxes}
                                />
                            </div>
                        </div>

                        {showAllTotals &&
                            <>
                                <div className="row">
                                    <div className="col-3">
                                        <Input
                                            label={`Seguro`}
                                            value={insurance}
                                            change={setInsurance}
                                            mask={`convertToMoney`}
                                        />
                                    </div>
                                    <div className="col-3">
                                        <Input
                                            label={`Outras despesas`}
                                            value={otherExpenses}
                                            change={setOtherExpenses}
                                            mask={`convertToMoney`}
                                        />
                                    </div>
                                    <div className={`col-3 totals-calculated ${calculating && autoTaxes ? 'stale' : ''}`}>
                                        <Input
                                            label={`Nº de itens`}
                                            value={String(totals?.items_count ?? items.length)}
                                            change={() => {}}
                                            disabled
                                        />
                                    </div>
                                    {totalField('approximate_taxes', 'Valor aprox. dos impostos')}
                                </div>
                                <div className="row">
                                    {totalField('icms_base', 'Base do ICMS')}
                                    {totalField('icms_value', 'Valor do ICMS')}
                                    {totalField('icms_st_base', 'Base do ICMS-ST')}
                                    {totalField('icms_st_value', 'Valor do ICMS-ST')}
                                </div>
                                <div className="row">
                                    {totalField('ipi_value', 'Valor do IPI')}
                                    {totalField('issqn_value', 'Valor do ISSQN')}
                                    {totalField('pis_value', 'Valor do PIS')}
                                    {totalField('cofins_value', 'Valor do COFINS')}
                                </div>
                                <div className="row">
                                    {totalField('fcp_value', 'Total do FCP')}
                                    {totalField('fcp_st_value', 'Total do FCP-ST')}
                                    {totalField('icms_exempt_value', 'ICMS desonerado')}
                                </div>
                            </>
                        }

                        <button
                            type="button"
                            className={`totals-toggle`}
                            onClick={() => setShowAllTotals(current => !current)}
                        >
                            {showAllTotals ? 'Mostrar menos' : 'Mostrar mais'}
                        </button>
                    </div>

                </Box>
            }

            {model === 'nfe' &&
                <Box>
                    <span className={`fiscal-issue-section`}>Transporte</span>

                    <div className="row">
                        <div className="col-6">
                            <InputSelect
                                menuInBody
                                label={`Frete por conta`}
                                options={FREIGHT_MODES}
                                value={freightMode}
                                change={setFreightMode}
                            />
                        </div>
                    </div>

                    {/* Sem transporte não há transportadora nem volume a
                        declarar — o resto da seção some. */}
                    {freightMode !== '9' &&
                        <>
                            <div className="row">
                                <div className="col-6">
                                    <SelectShippingCompany
                                        selectedShippingCompany={carrierId}
                                        setSelectedShippingCompany={setCarrierId}
                                        onSelectResource={fillCarrier}
                                        create={true}
                                    />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-6">
                                    <Input
                                        label={`Nome`}
                                        value={carrier.name}
                                        change={value => updateCarrier({ name: value })}
                                    />
                                </div>
                                <div className="col-3">
                                    <Input
                                        label={`CNPJ/CPF`}
                                        value={carrier.document}
                                        change={value => updateCarrier({ document: value })}
                                    />
                                </div>
                                <div className="col-3">
                                    <Input
                                        label={`Inscrição estadual`}
                                        value={carrier.ie}
                                        change={value => updateCarrier({ ie: value })}
                                    />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-6">
                                    <Input
                                        label={`Endereço`}
                                        value={carrier.street}
                                        change={value => updateCarrier({ street: value })}
                                    />
                                </div>
                                <div className="col-4">
                                    <Input
                                        label={`Município`}
                                        value={carrier.city}
                                        change={value => updateCarrier({ city: value })}
                                    />
                                </div>
                                <div className="col-2">
                                    <InputSelect
                                        menuInBody
                                        label={carrier.ie.trim() ? `UF *` : `UF`}
                                        options={states}
                                        value={carrier.state}
                                        change={value => updateCarrier({ state: value })}
                                        error={carrierNeedsState ? 'Obrigatória com IE' : null}
                                    />
                                </div>
                            </div>

                            <span className={`fiscal-issue-subsection`}>Volumes</span>

                            <div className="row">
                                <div className="col-2">
                                    <Input
                                        type={`number`}
                                        label={`Quantidade`}
                                        value={volume.quantity}
                                        change={value => setVolume(current => ({ ...current, quantity: value }))}
                                    />
                                </div>
                                <div className="col-3">
                                    <Input
                                        label={`Espécie`}
                                        value={volume.kind}
                                        change={value => setVolume(current => ({ ...current, kind: value }))}
                                    />
                                </div>
                                <div className="col-3">
                                    <Input
                                        label={`Marca`}
                                        value={volume.brand}
                                        change={value => setVolume(current => ({ ...current, brand: value }))}
                                    />
                                </div>
                                <div className="col-4">
                                    <Input
                                        label={`Numeração`}
                                        value={volume.number}
                                        change={value => setVolume(current => ({ ...current, number: value }))}
                                    />
                                </div>
                            </div>

                            <div className="row">
                                {/* Peso vem do cadastro dos produtos, somado
                                    pelas quantidades — se estiver zerado, é o
                                    catálogo que está sem peso. */}
                                <div className="col-3">
                                    <Input
                                        label={`Peso bruto (kg)`}
                                        value={weights.gross.toLocaleString('pt-br', { minimumFractionDigits: 3 })}
                                        change={() => {}}
                                        disabled
                                    />
                                </div>
                                <div className="col-3">
                                    <Input
                                        label={`Peso líquido (kg)`}
                                        value={weights.net.toLocaleString('pt-br', { minimumFractionDigits: 3 })}
                                        change={() => {}}
                                        disabled
                                    />
                                </div>
                                <div className="col-3">
                                    <Input
                                        label={`Placa do veículo`}
                                        value={vehicle.plate}
                                        change={value => setVehicle(current => ({ ...current, plate: value.toUpperCase() }))}
                                    />
                                </div>
                                <div className="col-3">
                                    <InputSelect
                                        menuInBody
                                        label={`UF do veículo`}
                                        options={states}
                                        value={vehicle.state}
                                        change={value => setVehicle(current => ({ ...current, state: value }))}
                                    />
                                </div>
                            </div>
                        </>
                    }
                </Box>
            }

            {model === 'nfe' &&
                <Box>
                    <span className={`fiscal-issue-section`}>Pagamento</span>
                    <div className="row">
                        <div className="col-6">
                            <InputSelect
                                menuInBody
                                label={`Forma de pagamento`}
                                options={paymentMethodOptions}
                                value={unpaidByOperation ? PAYMENT_NONE : paymentMethod}
                                change={changePaymentMethod}
                                loading={loadingPaymentOptions}
                                isDisabled={unpaidByOperation}
                            />
                        </div>
                        {!unpaid &&
                            <div className="col-6">
                                <InputSelect
                                    menuInBody
                                    label={`Condição de pagamento`}
                                    options={paymentTermOptions}
                                    value={paymentTermId ?? (paymentTerm ? 'legacy' : null)}
                                    change={changePaymentTerm}
                                    loading={loadingPaymentOptions}
                                    {...newPaymentTerm.selectProps}
                                />
                                {newPaymentTerm.modal}
                            </div>
                        }
                    </div>

                    {unpaid &&
                        <p className={`fiscal-issue-hint`}>
                            {unpaidByOperation
                                ? <>Devolução, remessa e bonificação saem sempre <strong>sem pagamento</strong> — a
                                    natureza escolhida define isso. Não há valor a receber nem duplicata.</>
                                : <>Esta nota sai declarada como <strong>sem pagamento</strong> (meio 90).
                                    Não há valor a receber nem duplicata.</>
                            }
                        </p>
                    }

                    {!unpaid && installments.length > 0 &&
                        <div className={`fiscal-issue-installments`}>
                            {awaitingTotal
                                ? <p className={`installments-empty`}>
                                    Os valores das parcelas aparecem quando a nota tiver itens com preço.
                                </p>
                                : installments.map((installment, index) => {
                                    const days = daysUntil(installment.due_date);
                                    return (
                                        <div className={`installments-row`} key={index}>
                                            <span className={`installments-number`}>{index + 1}ª</span>
                                            <input
                                                type="date"
                                                className={`installments-input`}
                                                aria-label={`Vencimento da ${index + 1}ª parcela`}
                                                value={installment.due_date}
                                                onChange={event => updateInstallment(index, { due_date: event.target.value })}
                                            />
                                            <span className={`installments-due ${days !== null && days < 0 ? 'overdue' : ''}`}>
                                                {describeDue(days)}
                                            </span>
                                            <input
                                                inputMode="numeric"
                                                className={`installments-input installments-amount`}
                                                aria-label={`Valor da ${index + 1}ª parcela`}
                                                value={convertIntToMoney(convertMoenyToInt(installment.amount), true)}
                                                onChange={event => updateInstallment(index, { amount: event.target.value })}
                                            />
                                            <button
                                                type="button"
                                                className={`installments-remove`}
                                                aria-label={`Remover a ${index + 1}ª parcela`}
                                                onClick={() => editInstallments(current => current.filter((_, i) => i !== index))}
                                            >
                                                <Icon name={`delete-small`} size={16} />
                                            </button>
                                        </div>
                                    );
                                })
                            }

                            {!awaitingTotal &&
                                <div className={`installments-foot`}>
                                    <button type="button" className={`installments-link`} onClick={addInstallment}>
                                        + Adicionar parcela
                                    </button>
                                    {/* A soma das parcelas precisa fechar com a nota,
                                        senão a SEFAZ recusa a duplicata. */}
                                    {totalCents > 0 && (installmentsTotal === totalCents
                                        ? <span className={`installments-total`}>
                                            <Icon name={`check`} size={14} strokeWidth={2.5} />
                                            Total {convertIntToMoney(totalCents, true)}
                                        </span>
                                        : <span className={`installments-total invalid`}>
                                            Soma {convertIntToMoney(installmentsTotal, true)} de {convertIntToMoney(totalCents, true)}
                                        </span>
                                    )}
                                </div>
                            }

                            {paymentTermId && installmentsEdited &&
                                <p className={`installments-edited`}>
                                    Parcelas ajustadas à mão.{' '}
                                    <button type="button" className={`installments-link`} onClick={() => setInstallmentsEdited(false)}>
                                        Recalcular pela condição
                                    </button>
                                </p>
                            }
                        </div>
                    }
                </Box>
            }

            {model === 'nfe' &&
                <Box>
                    <span className={`fiscal-issue-section`}>Dados adicionais</span>

                    <InputTextarea
                        label={`Observações`}
                        rows={3}
                        value={comments}
                        change={setComments}
                    />

                    {/* Travados: vêm da natureza de operação. Editar aqui
                        criaria divergência com o que está cadastrado. */}
                    {operationNotes.taxpayer &&
                        <>
                            <InputTextarea
                                label={`Observações da natureza de operação`}
                                rows={2}
                                value={operationNotes.taxpayer}
                                change={() => {}}
                                disabled
                            />
                            <p className={`fiscal-issue-hint`}>
                                Sai na nota junto com as suas observações. Para mudar, edite a
                                natureza de operação.
                            </p>
                        </>
                    }

                    {operationNotes.fisco &&
                        <InputTextarea
                            label={`Informações ao Fisco`}
                            rows={2}
                            value={operationNotes.fisco}
                            change={() => {}}
                            disabled
                        />
                    }
                </Box>
            }

            {model === 'nfse' &&
                <Box>
                    <span className={`fiscal-issue-section`}>Serviço prestado</span>
                    <InputTextarea
                        label={`Descrição do serviço`}
                        value={serviceDescription}
                        change={setServiceDescription}
                        rows={3}
                    />
                    <div className="row">
                        <div className="col-4">
                            <Input
                                label={`Valor do serviço`}
                                value={serviceAmount}
                                change={setServiceAmount}
                                mask={`convertToMoney`}
                            />
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-5">
                            <InputSelect
                                menuInBody
                                creatable
                                createLabel={input => `Usar o código "${input}" (fora da lista nacional)`}
                                label={`Código do serviço`}
                                options={serviceCodes}
                                value={serviceCode}
                                change={setServiceCode}
                            />
                        </div>
                        <div className="col-4">
                            <Input
                                label={`Código na prefeitura`}
                                value={cityServiceCode}
                                change={setCityServiceCode}
                            />
                        </div>
                        <div className="col-3">
                            <Input
                                label={`Alíquota de ISS (%)`}
                                value={issRate}
                                change={setIssRate}
                            />
                        </div>
                    </div>
                    <p className={`fiscal-issue-hint`}>
                        Se a sua prefeitura usa numeração própria, digite o código dela que a
                        opção de usar aparece. O código na prefeitura é opcional.
                    </p>
                    <p className={`fiscal-issue-hint`}>
                        Qual código e qual alíquota informar é definição do
                        <strong> seu contador</strong>.
                    </p>

                    {/* Retenção é a exceção: fica atrás de um interruptor para
                        não pesar a tela de quem nunca vai usar. */}
                    <div className={`fiscal-switch-row`}>
                        <div>
                            <strong>O tomador retém impostos nesta nota</strong>
                            <span>
                                A nota sai pelo valor cheio e informa as retenções separadamente.
                            </span>
                        </div>
                        <Toggle
                            checked={showWithholdings}
                            onChange={setShowWithholdings}
                            label={`Tem retenção`}
                        />
                    </div>

                    {showWithholdings &&
                        <>
                            <div className={`fiscal-switch-row`}>
                                <div>
                                    <strong>ISS retido pelo tomador</strong>
                                    <span>
                                        Marque quando quem recolhe o ISS é o cliente. A alíquota
                                        continua sendo a informada acima.
                                    </span>
                                </div>
                                <Toggle
                                    checked={issWithheld}
                                    onChange={setIssWithheld}
                                    label={`ISS retido`}
                                />
                            </div>

                            <div className="row">
                                {WITHHOLDING_FIELDS.map(field => (
                                    <div className="col-2" key={field.key}>
                                        <Input
                                            label={field.label}
                                            value={withholdings[field.key]}
                                            change={value => setWithholdings(current => ({
                                                ...current,
                                                [field.key]: value,
                                            }))}
                                        />
                                    </div>
                                ))}
                            </div>
                            <div className="row">
                                <div className="col-4">
                                    <Input
                                        label={`Outras retenções (R$)`}
                                        value={withholdings.others}
                                        change={value => setWithholdings(current => ({
                                            ...current,
                                            others: value,
                                        }))}
                                        mask={`convertToMoney`}
                                    />
                                </div>
                            </div>
                            <p className={`fiscal-issue-hint`}>
                                Informe as <strong>alíquotas</strong>: o valor de cada retenção é
                                calculado pelo emissor sobre a base do serviço. Deixe em branco o
                                que não for retido. Quais impostos o seu cliente retém, e em que
                                percentual, é definição da <strong>contabilidade dele</strong>.
                            </p>
                        </>
                    }
                </Box>
            }

            </div>

            <Box>
                <div className={`fiscal-issue-footer`}>
                    <span className={`issue-total`}>
                        Total da nota: <strong>{convertIntToMoney(totalCents, true)}</strong>
                    </span>
                    <div className={`issue-actions`}>
                        <Button
                            type={`secondary`}
                            size={`small`}
                            text={readOnly ? `Voltar` : `Cancelar`}
                            link={`/fiscal/documents`}
                        />
                        {!readOnly &&
                            <Button
                                type={`primary`}
                                size={`small`}
                                text={`Salvar nota`}
                                action={issue}
                                loading={issuing}
                                disabled={!canIssue}
                            />
                        }
                    </div>
                </div>
            </Box>
        </PanelTemplate>
    );
};

export default FiscalIssueDocument;
